"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Iconify from "@/components/ui/Iconify";
import { ListTile, TileRating } from "@/components/kit";
import { getMediaRecordUrl } from "@/utils/media";
import { formatDistance } from "@/utils/helpers";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";
import { getPlaceIdentifier } from "@/utils/placeUtils";
import NoPlacePlaceholder from "@/components/places-comps/place-list/no-place-placeholder";

const ACCENT = "#e5136f";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Pick the first value that parses to a finite number > 0. Imported places
// have averageDbRating = 0 (no in-app reviews); `??` would stop there and hide
// the real Naver rating, so we must skip 0 and fall through to naverRating.
const firstPositiveRating = (...values) => {
  for (const v of values) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const getRating = (place) =>
  firstPositiveRating(
    place?.averageDbRating,
    place?.naverRating,
    place?.nRating,
    place?.averageRating
  );

const getReviewCount = (place) =>
  Math.max(
    toNumber(place?.totalDbReviews),
    toNumber(place?.numberOfVisitorReviews),
    toNumber(place?.totalReviews)
  );

// 목록 한 줄은 공용 부품을 쓴다(디자인 기준 v2). 예전에는 이 화면만의 크기·색이라
// 웹 플레이스·검색 결과·프로필과 모양이 제각각이었다(별이 여기만 핑크였다).
const SheetRow = ({ place, onClick }) => {
  const hasMedia = Array.isArray(place?.placeMedia) && place.placeMedia.length > 0;
  const thumb = hasMedia
    ? getMediaRecordUrl(place.placeMedia[0], {
        variant: "thumbnail",
        preferPlaceholder: true,
      })
    : "";
  const rating = getRating(place);
  const reviews = getReviewCount(place);
  const distance = !isNaN(Number(place?.distanceFromMe))
    ? formatDistance(Number(place?.distanceFromMe))
    : "";
  const subtitle = [place?.type, place?.subcategory, distance]
    .filter(Boolean)
    .join(" · ");

  return (
    <ListTile
      verified={Boolean(place?.storeAccountVerifiedAt)}
      imageUrl={thumb}
      title={place?.name}
      subtitle={subtitle}
      meta={
        rating > 0 ? (
          <TileRating rating={rating} source="네이버" reviews={reviews} />
        ) : null
      }
      onClick={() => onClick(place)}
      right={
        <Iconify icon="mdi:chevron-right" className="!h-5 !w-5 shrink-0 text-ink-25" />
      }
      className="px-[18px]"
    />
  );
};

const SORTS = [
  { key: "distance", label: "가까운순" },
  { key: "rating", label: "평점순" },
  { key: "numOfReviews", label: "리뷰순" },
];

const PlacesMobileBottomSheet = ({
  places = [],
  loader,
  infiniteScrollLoader,
  totalPlacesAvailable = 0,
  setListPageNo,
  handlePlaceSorting,
  openPlaceAddModal,
  showMyLocation,
  onZoomIn,
  onZoomOut,
  currentUserLocation,
  onRowSelect,
  previewPlace,
  onPreviewClose,
  hasSelection = false,
  onClearSelection,
}) => {
  const { routeToPlaceDetail } = usePlaceViewNavigation();
  const sheetRef = useRef(null);
  const scrollerRef = useRef(null);
  const dragRef = useRef({ dragging: false, startY: 0, startTy: 0, lastTy: 0 });
  // Drag-from-body state (raise/collapse the sheet by dragging on the list, not
  // only on the handle). Kept in a ref so the non-passive touch handlers below
  // never trigger React re-renders mid-drag.
  const bodyDragRef = useRef({
    active: false,
    startY: 0,
    startTy: 0,
    lastTy: 0,
    mode: "idle",
  });
  const snapYRef = useRef(0);
  const snapsRef = useRef({ FULL: 10, HALF: 0, PEEK: 0 });
  const [containerH, setContainerH] = useState(0);
  const [snapY, setSnapY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [sortIndex, setSortIndex] = useState(0);
  const [sortOpen, setSortOpen] = useState(false);

  const snaps = useMemo(() => {
    const h = containerH || 640;
    const FULL = 10;
    const HALF = Math.round(h * 0.46);
    const PEEK = Math.max(h - 176, HALF + 40);
    return { FULL, HALF, PEEK };
  }, [containerH]);

  // Mirror snap state into refs so the body-drag touch handlers (attached once,
  // non-passive) always read the current values without re-attaching.
  useEffect(() => {
    snapYRef.current = snapY;
  }, [snapY]);
  useEffect(() => {
    snapsRef.current = snaps;
  }, [snaps]);

  // measure the map container height and start at PEEK
  useEffect(() => {
    const measure = () => {
      const parent = sheetRef.current?.parentElement;
      if (parent) setContainerH(parent.clientHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    // (re)snap to PEEK whenever the container size is first known
    if (containerH) setSnapY((prev) => (prev === 0 ? snaps.PEEK : prev));
  }, [containerH, snaps.PEEK]);

  const nearestSnap = useCallback(
    (ty) => {
      const points = [snaps.FULL, snaps.HALF, snaps.PEEK];
      return points.reduce(
        (best, p) => (Math.abs(p - ty) < Math.abs(best - ty) ? p : best),
        points[0]
      );
    },
    [snaps]
  );

  const onPointerMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      let ty = d.startTy + (clientY - d.startY);
      ty = Math.min(snaps.PEEK, Math.max(snaps.FULL, ty));
      d.lastTy = ty;
      if (sheetRef.current) sheetRef.current.style.transform = `translateY(${ty}px)`;
      if (e.cancelable) e.preventDefault();
    },
    [snaps]
  );

  const onPointerUp = useCallback(() => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dragging = false;
    setDragging(false);
    window.removeEventListener("mousemove", onPointerMove);
    window.removeEventListener("touchmove", onPointerMove);
    window.removeEventListener("mouseup", onPointerUp);
    window.removeEventListener("touchend", onPointerUp);
    setSnapY(nearestSnap(d.lastTy));
  }, [nearestSnap, onPointerMove]);

  const onPointerDown = useCallback(
    (e) => {
      const d = dragRef.current;
      d.dragging = true;
      d.startY = e.touches ? e.touches[0].clientY : e.clientY;
      d.startTy = snapY;
      d.lastTy = snapY;
      setDragging(true);
      window.addEventListener("mousemove", onPointerMove);
      window.addEventListener("touchmove", onPointerMove, { passive: false });
      window.addEventListener("mouseup", onPointerUp);
      window.addEventListener("touchend", onPointerUp);
    },
    [snapY, onPointerMove, onPointerUp]
  );

  // Drag-to-expand from the LIST body (not just the handle) — mirrors how
  // Naver/Kakao map sheets behave:
  //   • While the sheet is BELOW full height, a drag anywhere in the list moves
  //     the SHEET and the inner list stays locked (touchmove is prevented).
  //   • Once at FULL, upward drags scroll the inner list; a downward drag while
  //     the list is at scrollTop 0 collapses the sheet.
  // The touchmove listener MUST be non-passive so preventDefault() can stop the
  // gesture from bubbling to Safari (page rubber-band / toolbar move). React's
  // onTouchMove is passive, so we attach manually via addEventListener.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const clientY = (e) =>
      e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;

    const onStart = (e) => {
      const b = bodyDragRef.current;
      if (e.touches && e.touches.length > 1) {
        b.active = false;
        return;
      }
      b.active = true;
      b.startY = clientY(e);
      b.startTy = snapYRef.current;
      b.lastTy = snapYRef.current;
      b.mode = "undecided"; // undecided | sheet | scroll
    };

    const onMove = (e) => {
      const b = bodyDragRef.current;
      if (!b.active) return;
      const s = snapsRef.current;
      const dy = clientY(e) - b.startY; // + down, - up
      const atFull = b.startTy <= s.FULL + 1;

      if (b.mode === "undecided") {
        if (Math.abs(dy) < 6) return; // wait for a clear intent
        if (!atFull) {
          b.mode = "sheet"; // below full: any drag drives the sheet
        } else if (dy > 0 && scroller.scrollTop <= 0) {
          b.mode = "sheet"; // at full, pulling down from top: collapse
        } else {
          b.mode = "scroll"; // at full: let the inner list scroll natively
        }
        if (b.mode === "sheet") {
          const el = sheetRef.current;
          if (el) {
            el.style.transition = "none";
            el.style.willChange = "transform";
          }
        }
      }

      if (b.mode === "sheet") {
        let ty = b.startTy + dy;
        ty = Math.min(s.PEEK, Math.max(s.FULL, ty));
        b.lastTy = ty;
        if (sheetRef.current) {
          sheetRef.current.style.transform = `translateY(${ty}px)`;
        }
        if (e.cancelable) e.preventDefault(); // lock inner scroll + page
      }
    };

    const onEnd = () => {
      const b = bodyDragRef.current;
      if (!b.active) return;
      b.active = false;
      if (b.mode === "sheet") {
        const el = sheetRef.current;
        if (el) {
          // Restore class-based transition so the settle animates.
          el.style.transition = "";
          el.style.willChange = "";
        }
        const s = snapsRef.current;
        const points = [s.FULL, s.HALF, s.PEEK];
        const nearest = points.reduce(
          (best, p) => (Math.abs(p - b.lastTy) < Math.abs(best - b.lastTy) ? p : best),
          points[0]
        );
        setSnapY(nearest);
      }
      b.mode = "idle";
    };

    scroller.addEventListener("touchstart", onStart, { passive: true });
    scroller.addEventListener("touchmove", onMove, { passive: false });
    scroller.addEventListener("touchend", onEnd);
    scroller.addEventListener("touchcancel", onEnd);
    return () => {
      scroller.removeEventListener("touchstart", onStart);
      scroller.removeEventListener("touchmove", onMove);
      scroller.removeEventListener("touchend", onEnd);
      scroller.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const applySort = (idx) => {
    setSortIndex(idx);
    setSortOpen(false);
    if (typeof handlePlaceSorting === "function") {
      handlePlaceSorting(SORTS[idx].key);
    }
  };

  const onRowClick = (place) => {
    onRowSelect?.(place); // highlight this place's pin on the map behind
    routeToPlaceDetail(place);
  };

  // The map-tapped place is shown as a highlighted row at the TOP of the list
  // (not a separate floating card — that overlapped the sheet). Scroll it into
  // view when the selection changes.
  const previewId =
    previewPlace?.placeUniqueNumber ??
    previewPlace?._id ??
    previewPlace?.id ??
    null;
  useEffect(() => {
    if (!previewId) return;
    const el = document.getElementById("places-sheet-scroll");
    if (el) el.scrollTop = 0;
  }, [previewId]);

  const hasMore =
    places?.length < 100 &&
    places?.length < totalPlacesAvailable &&
    places?.length > 0;

  const count = totalPlacesAvailable || places?.length || 0;

  return (
    <>
      {/* floating map controls: zoom +/- stacked above the my-location button
          (sits above the peeking sheet, right side — Kakao/Naver map style) */}
      {/* 고른 가게가 있을 때만 뜬다. 분홍은 행동에만 쓰는 규칙이라 여기엔 안 쓴다
          — 흰 알약에 회색 ✕. 누르면 지도의 테와 미리보기가 함께 사라진다. */}
      {hasSelection && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-40"
          style={{ bottom: (containerH ? containerH - snaps.PEEK : 176) + 16 }}
        >
          <button
            type="button"
            onClick={() => onClearSelection?.()}
            className="flex items-center gap-1.5 rounded-full bg-white dark:bg-brown/90 border border-black/10 dark:border-white/10 px-3 py-1.5 text-[12px] font-bold text-black/80 dark:text-white/85 shadow-md active:scale-95 transition"
            aria-label="선택 해제"
          >
            <Iconify icon="mdi:close" className="!h-3.5 !w-3.5 text-black/50 dark:text-white/60" />
            선택 해제
          </button>
        </div>
      )}
      <div
        className="absolute right-4 z-40 flex flex-col items-center gap-2"
        style={{ bottom: (containerH ? containerH - snaps.PEEK : 176) + 16 }}
      >
        {(onZoomIn || onZoomOut) && (
          <div className="flex flex-col rounded-2xl bg-white dark:bg-brown/90 border border-black/10 dark:border-white/10 shadow-md overflow-hidden">
            <button
              type="button"
              onClick={() => onZoomIn?.()}
              className="p-2 active:scale-90 transition"
              aria-label="확대"
            >
              <Iconify icon="mdi:plus" className="!h-5 !w-5 text-black/70 dark:text-white/80" />
            </button>
            <div className="h-px w-5 mx-auto bg-black/10 dark:bg-white/15" />
            <button
              type="button"
              onClick={() => onZoomOut?.()}
              className="p-2 active:scale-90 transition"
              aria-label="축소"
            >
              <Iconify icon="mdi:minus" className="!h-5 !w-5 text-black/70 dark:text-white/80" />
            </button>
          </div>
        )}
        {currentUserLocation && (
          <button
            type="button"
            onClick={() => showMyLocation?.(currentUserLocation)}
            className="bg-white dark:bg-brown/90 border border-black/10 dark:border-white/10 rounded-full p-2 shadow-md active:scale-90 transition"
            aria-label="내 위치"
          >
            <Iconify icon="mdi:my-location" className="!h-5 !w-5" style={{ color: ACCENT }} />
          </button>
        )}
      </div>

      <div
        ref={sheetRef}
        // 지도가 이름표를 그릴 때 이 시트가 어디까지 올라와 있는지 물어본다.
        // 시트에 가리는 자리에는 이름표를 안 그린다(2026-08-09 사장님 지적).
        data-places-sheet=""
        className={`absolute left-0 right-0 bottom-0 z-40 bg-white dark:bg-dark_bg_grey rounded-t-[20px] shadow-[0_-6px_24px_rgba(0,0,0,0.14)] flex flex-col ${
          dragging ? "" : "transition-transform duration-300 ease-out"
        }`}
        style={{
          height: "100%",
          transform: `translateY(${snapY}px)`,
          // Only hint a compositing layer WHILE dragging. Keeping
          // will-change:transform permanently pinned the sheet to a GPU layer
          // that iOS Safari wouldn't repaint when thumbnails finished loading.
          willChange: dragging ? "transform" : "auto",
        }}
      >
        {/* drag zone: grip + header */}
        <div
          className="flex-none cursor-grab select-none"
          style={{ touchAction: "none" }}
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
        >
          <div className="pt-[9px] pb-1">
            <div className="w-10 h-[5px] rounded-full bg-[#dcdfe3] dark:bg-white/20 mx-auto" />
          </div>
          <div className="flex items-baseline justify-between px-[18px] pt-1 pb-3">
            <div className="text-[15px] font-extrabold tracking-tight text-[#262626] dark:text-white">
              이 지역 <span className="text-black/70 dark:text-white/75">{count}곳</span>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOpen((o) => !o);
                }}
                className="text-[12.5px] font-bold text-[#8a8f98] dark:text-white/60 flex items-center gap-0.5"
              >
                {SORTS[sortIndex].label}
                <Iconify icon="mdi:chevron-down" className="!h-4 !w-4 !text-inherit" />
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-6 z-50 bg-white dark:bg-heading_black border border-black/10 dark:border-white/10 rounded-lg shadow-lg overflow-hidden">
                  {SORTS.map((s, idx) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        applySort(idx);
                      }}
                      className={`block w-full text-start px-4 py-2 text-[13px] whitespace-nowrap ${
                        idx === sortIndex
                          ? "font-bold text-[#262626] dark:text-white"
                          : "text-black/60 dark:text-white/60"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* scrollable list */}
        <div
          id="places-sheet-scroll"
          ref={scrollerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden thin-scrollbar overscroll-contain pb-[calc(env(safe-area-inset-bottom,0px)+48px)]"
        >
          {previewPlace && (
            <div className="relative border-b-[6px] border-black/[0.06] bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.05]">
              <div className="flex items-center justify-between px-[18px] pt-2 pb-1">
                <span className="text-[11px] font-extrabold tracking-tight text-black/60 dark:text-white/65">
                  지도에서 선택한 장소
                </span>
                <button
                  type="button"
                  aria-label="close"
                  onClick={onPreviewClose}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-black/5 text-black/40 dark:bg-white/10 dark:text-white/50"
                >
                  <Iconify icon="mingcute:close-line" className="!h-3.5 !w-3.5" />
                </button>
              </div>
              <SheetRow place={previewPlace} onClick={onRowClick} />
            </div>
          )}
          {loader ? (
            <div className="flex flex-col">
              {[0, 1, 2, 3, 4].map((k) => (
                <div key={k} className="flex items-center gap-3 px-[18px] py-[11px] border-b border-[#f4f5f6] dark:border-brown">
                  <div className="w-[54px] h-[72px] rounded-[9px] bg-zinc-200 dark:bg-zinc-700 animate-pulse flex-none" />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-4 w-2/3 bg-zinc-200 dark:bg-zinc-700 rounded-full animate-pulse" />
                    <div className="h-3 w-1/2 bg-zinc-200 dark:bg-zinc-700 rounded-full animate-pulse" />
                    <div className="h-3 w-1/3 bg-zinc-200 dark:bg-zinc-700 rounded-full animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : places?.length === 0 ? (
            <NoPlacePlaceholder openPlaceAddModal={openPlaceAddModal} />
          ) : (
            <InfiniteScroll
              dataLength={places?.length}
              next={() => setListPageNo((prev) => prev + 1)}
              hasMore={hasMore}
              scrollThreshold={0.85}
              scrollableTarget="places-sheet-scroll"
              loader={
                <div className="py-5 text-black/40 dark:text-white/40 text-xs flex items-center justify-center gap-1">
                  <Iconify className="!text-inherit" icon="eos-icons:loading" />
                  더 불러오는 중...
                </div>
              }
              endMessage={
                <div className="py-6 text-center text-[12px] text-black/35 dark:text-white/35">
                  이 지역의 결과를 모두 봤어요
                </div>
              }
            >
              {places
                .filter((place) => {
                  if (!previewId) return true;
                  const k =
                    place?.placeUniqueNumber ?? place?._id ?? place?.id;
                  return String(k) !== String(previewId);
                })
                .map((place, i) => (
                  <SheetRow
                    key={
                      getPlaceIdentifier(place) || place?._id || place?.id || i
                    }
                    place={place}
                    onClick={onRowClick}
                  />
                ))}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </>
  );
};

export default PlacesMobileBottomSheet;
